// Admin client for /admin/affiliates — mirrors com.platform.marketing.controller.admin.AdminAffiliateController.
// List endpoint returns a plain array (same reasoning as advertisers).
import { api } from "@/lib/api";
import type { AssignmentLogEntryDto } from "@/api/advertisers";

export interface AffiliateDto {
    id: number;
    slug: string;
    name: string;
    contactEmail: string | null;
    payoutEmail: string | null;
    commissionBps: number | null;
    payoutTerms: string | null;
    notes: string | null;
    status: string;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateAffiliateRequest {
    slug: string;
    name: string;
    contactEmail?: string | null;
    payoutEmail?: string | null;
    commissionBps?: number | null;
    payoutTerms?: string | null;
    notes?: string | null;
}

export interface UpdateAffiliateRequest {
    name?: string | null;
    contactEmail?: string | null;
    payoutEmail?: string | null;
    commissionBps?: number | null;
    payoutTerms?: string | null;
    notes?: string | null;
    status?: string | null;
}

export interface AffiliateDashboardDto {
    affiliateId: number;
    slug: string;
    name: string;
    status: string;
    campaignsOwned: number;
    clicksAllTime: number;
    leadsAllTime: number;
    conversionRatePct: number | null;
}

export async function listAffiliates(includeArchived = false): Promise<AffiliateDto[]> {
    const { data } = await api.get<AffiliateDto[]>("/admin/affiliates", {
        params: { includeArchived },
    });
    return data;
}

export async function getAffiliate(id: number): Promise<AffiliateDto> {
    const { data } = await api.get<AffiliateDto>(`/admin/affiliates/${id}`);
    return data;
}

export async function createAffiliate(req: CreateAffiliateRequest): Promise<AffiliateDto> {
    const { data } = await api.post<AffiliateDto>("/admin/affiliates", req);
    return data;
}

export async function updateAffiliate(id: number, req: UpdateAffiliateRequest): Promise<AffiliateDto> {
    const { data } = await api.patch<AffiliateDto>(`/admin/affiliates/${id}`, req);
    return data;
}

export async function archiveAffiliate(id: number): Promise<void> {
    await api.post(`/admin/affiliates/${id}/archive`);
}

/** Hard-delete. Backend returns 409 if any FK refs exist (campaigns/ad_sources). */
export async function deleteAffiliate(id: number): Promise<void> {
    await api.delete(`/admin/affiliates/${id}`);
}

export async function getAffiliateDashboard(id: number): Promise<AffiliateDashboardDto> {
    const { data } = await api.get<AffiliateDashboardDto>(`/admin/affiliates/${id}/dashboard`);
    return data;
}

export async function getAffiliateAssignmentLog(id: number, limit = 50): Promise<AssignmentLogEntryDto[]> {
    const { data } = await api.get<AssignmentLogEntryDto[]>(
        `/admin/affiliates/${id}/assignment-log`,
        { params: { limit } },
    );
    return data;
}

export interface AssignAffiliateBody {
    affiliateId: number | null;
    reason?: string | null;
}

export async function assignCampaignAffiliate(campaignId: number, body: AssignAffiliateBody): Promise<void> {
    await api.patch(`/admin/campaigns/${campaignId}/affiliate`, body);
}

export async function assignAdSourceAffiliate(adSourceId: number, body: AssignAffiliateBody): Promise<void> {
    await api.patch(`/admin/ad-sources/${adSourceId}/affiliate`, body);
}
